import React, { useState } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  FileText,
  Download,
  Calendar,
  TrendingUp,
  BarChart3,
  PieChart as PieChartIcon,
  FileBarChart,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

export default function ReportsPage() {
  const [selectedReport, setSelectedReport] = useState('revenue');
  const [timeRange, setTimeRange] = useState('6months');

  const reports = [
    {
      id: 'revenue',
      name: 'Revenue Report',
      description: 'Comprehensive revenue analysis and trends',
      icon: TrendingUp,
      type: 'line',
    },
    {
      id: 'operators',
      name: 'Operator Performance',
      description: 'Detailed operator metrics and KPIs',
      icon: BarChart3,
      type: 'bar',
    },
    {
      id: 'market',
      name: 'Market Analysis',
      description: 'Market trends and competitive insights',
      icon: PieChartIcon,
      type: 'line',
    },
    {
      id: 'compliance',
      name: 'Compliance Report',
      description: 'Regulatory compliance and audit trails',
      icon: FileBarChart,
      type: 'bar',
    },
  ];

  const revenueData = [
    { month: 'Jan', revenue: 185000, target: 180000, growth: 12 },
    { month: 'Feb', revenue: 215000, target: 200000, growth: 16 },
    { month: 'Mar', revenue: 245000, target: 220000, growth: 14 },
    { month: 'Apr', revenue: 275000, target: 250000, growth: 12 },
    { month: 'May', revenue: 295000, target: 280000, growth: 7 },
    { month: 'Jun', revenue: 325000, target: 300000, growth: 10 },
  ];

  const operatorData = [
    { name: 'Green Valley', performance: 92, revenue: 245000, satisfaction: 4.8 },
    { name: 'Pacific Coast', performance: 88, revenue: 189000, satisfaction: 4.6 },
    { name: 'Mountain View', performance: 85, revenue: 156000, satisfaction: 4.5 },
    { name: 'Sunrise Gardens', performance: 95, revenue: 298000, satisfaction: 4.9 },
    { name: 'Northern Lights', performance: 90, revenue: 334000, satisfaction: 4.7 },
  ];

  const handleDownloadReport = () => {
    const reportName = reports.find((r) => r.id === selectedReport)?.name;
    toast.success(`${reportName} downloaded successfully`);
  };

  const handleGenerateReport = () => {
    toast.success('Report generated successfully');
  };

  const renderChart = () => {
    if (selectedReport === 'revenue' || selectedReport === 'market') {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={revenueData}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
            <YAxis stroke="hsl(var(--muted-foreground))" />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="revenue"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              dot={{ fill: 'hsl(var(--primary))' }}
            />
            <Line
              type="monotone"
              dataKey="target"
              stroke="hsl(var(--accent))"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ fill: 'hsl(var(--accent))' }}
            />
          </LineChart>
        </ResponsiveContainer>
      );
    } else {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={operatorData}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
            <YAxis stroke="hsl(var(--muted-foreground))" />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
            />
            <Legend />
            <Bar dataKey="performance" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    }
  };

  const getReportStats = () => {
    if (selectedReport === 'revenue') {
      return [
        { label: 'Total Revenue', value: '$1,844,000', change: '+12.5%' },
        { label: 'Avg Monthly', value: '$307,333', change: '+8.3%' },
        { label: 'Growth Rate', value: '11.8%', change: '+2.1%' },
        { label: 'Target Achievement', value: '108%', change: '+5.2%' },
      ];
    } else {
      return [
        { label: 'Total Operators', value: '1,247', change: '+12.5%' },
        { label: 'Avg Performance', value: '90%', change: '+4.2%' },
        { label: 'Top Performer', value: 'Sunrise Gardens', change: '95%' },
        { label: 'Satisfaction', value: '4.7/5', change: '+0.3' },
      ];
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Report Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {reports.map((report) => {
            const Icon = report.icon;
            const isSelected = selectedReport === report.id;
            return (
              <Card
                key={report.id}
                className={`cursor-pointer transition-all hover:shadow-lg ${
                  isSelected ? 'border-primary shadow-glow' : ''
                }`}
                onClick={() => setSelectedReport(report.id)}
              >
                <CardHeader>
                  <div
                    className={`w-12 h-12 rounded-lg flex items-center justify-center mb-2 ${
                      isSelected ? 'gradient-primary' : 'bg-primary/10'
                    }`}
                  >
                    <Icon
                      className={`w-6 h-6 ${
                        isSelected ? 'text-white' : 'text-primary'
                      }`}
                    />
                  </div>
                  <CardTitle className="text-base">{report.name}</CardTitle>
                  <CardDescription className="text-xs">{report.description}</CardDescription>
                </CardHeader>
              </Card>
            );
          })}
        </div>

        {/* Report Controls */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <CardTitle>
                  {reports.find((r) => r.id === selectedReport)?.name}
                </CardTitle>
                <CardDescription>
                  {reports.find((r) => r.id === selectedReport)?.description}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
                  <option value="1month">Last Month</option>
                  <option value="3months">Last 3 Months</option>
                  <option value="6months">Last 6 Months</option>
                  <option value="1year">Last Year</option>
                </Select>
                <Button onClick={handleGenerateReport}>
                  <FileText className="w-4 h-4 mr-2" />
                  Generate
                </Button>
                <Button variant="outline" onClick={handleDownloadReport}>
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Report Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {getReportStats().map((stat, index) => (
            <Card key={index}>
              <CardHeader className="pb-2">
                <CardDescription className="text-xs">{stat.label}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-foreground">{stat.value}</div>
                <Badge variant="success" className="text-xs mt-1">
                  {stat.change}
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Data Visualization</CardTitle>
            <CardDescription>Detailed analysis for the selected time range</CardDescription>
          </CardHeader>
          <CardContent>{renderChart()}</CardContent>
        </Card>

        {/* Report Details */}
        <Card>
          <CardHeader>
            <CardTitle>Report Summary</CardTitle>
            <CardDescription>Key insights and recommendations</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-foreground mb-2">Key Findings</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 mr-2 flex-shrink-0" />
                    Revenue has shown consistent growth over the past 6 months with an average
                    increase of 11.8%
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 mr-2 flex-shrink-0" />
                    Target achievement rate has improved significantly, reaching 108% in the
                    latest quarter
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 mr-2 flex-shrink-0" />
                    Top-performing operators are driving 65% of total revenue growth
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-2">Recommendations</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 mr-2 flex-shrink-0" />
                    Focus on scaling successful operator strategies to underperforming regions
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 mr-2 flex-shrink-0" />
                    Increase investment in high-growth markets identified in the analysis
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 mr-2 flex-shrink-0" />
                    Implement quarterly reviews to maintain current growth trajectory
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}